import { Button as DsButton } from "@/design-system/Button/Button";
import { RoleSelectSheetContent } from "@/design-system/RoleSelectPanel/RoleSelectPanel";
import { PlayerFiltersPanel } from "@/pages/Players/FiltersPanel/PlayerFiltersPanel";
import {
  RangeFilterType,
  SelectorFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { StickySidebar } from "@/pages/Players/StickySidebar";
import {
  applyEvolutionFilters,
  evolutionFiltersSelector,
  evolutionsBuilderSelector,
  setEvolutionBuilderActiveFilter,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import {
  evolutionsSelector,
  setActivePickerSelection,
  setIncludedEvolutions,
  setMustIncludeEvolutions,
} from "@/redux/evolutions/evolutionsSlice";
import {
  PlayerRole,
  activeFiltersOf,
  clearAllFilters,
  clearFilter,
  defaultPositions,
  updateFilter,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import type { PlayersSortByType } from "@repo/shared/api";
import type { EvolutionPickerSelection } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { buildRoleGroups } from "@repo/shared/utils";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { EvolutionBuilderFilters } from "../EvolutionsBuilder/components/EvolutionBuilderFilters";
import { IncludeExcludeEvolutions } from "../EvolutionsBuilder/components/IncludeExcludeEvolutions/IncludeExcludeEvolutions";
import { ToolbarAction } from "../EvolutionsPage/EvolutionsPage";

interface Props {
  elementOpened: ToolbarAction;
  handleToolbarAction: (action: ToolbarAction) => void;
  isTouchDevice: boolean;
  playerRoles: PlayerRole[];
  playerRole?: PlayerRole;
  handleSelectPlayerRole?: (playerRole: PlayerRole) => void;
  // Multi-select player roles (used in evolution builder)
  selectedPlayerRoles?: PlayerRole[];
  handleSelectPlayerRoles?: (playerRoles: PlayerRole[]) => void;
  refPlayerRole: React.RefObject<HTMLDivElement>;
  refFilters?: React.RefObject<HTMLDivElement>;
  refIncludeExclude?: React.RefObject<HTMLDivElement>;
  hideRolesPlayerCannotPlay?: string[];
  // Optional props for standard player filters (used in EvolutionsPlayersSection)
  playerFilters?: (SelectorFilterType | RangeFilterType)[];
  sortBy?: PlayersSortByType;
  setSortBy?: (sortBy: PlayersSortByType) => void;
  // When true, uses MultiSelectPlayerRole (e.g. evolutions players tab); does not imply builder filters
  multiSelectPlayerRoles?: boolean;
  builderContext?: "evolution-builder" | "evolution-builder-with-player";
  onFiltersUpdated: () => void;
}

export const EvolutionsStickySidebar = ({
  elementOpened,
  handleToolbarAction,
  isTouchDevice,
  playerRoles,
  playerRole,
  refPlayerRole,
  refFilters,
  refIncludeExclude,
  handleSelectPlayerRole,
  selectedPlayerRoles,
  handleSelectPlayerRoles,
  hideRolesPlayerCannotPlay,
  multiSelectPlayerRoles,
  playerFilters,
  sortBy,
  setSortBy,
  builderContext,
  onFiltersUpdated,
}: Props) => {
  const { t } = useTranslation(["evolutions", "players", "common"]);
  const { activePickerSelection } = useSelector(evolutionsSelector);
  const { activeFilter } = useSelector(evolutionsBuilderSelector);
  const {
    includedEvolutions: builderIncludedEvolutions,
    mustIncludeEvolutions: builderMustIncludeEvolutions,
  } = useSelector(evolutionFiltersSelector);
  const {
    includedEvolutions: evoPlayersIncludedEvolutions,
    mustIncludeEvolutions: evoPlayersMustIncludeEvolutions,
  } = useSelector(evolutionsSelector);
  // Tracks the filters once per panel session, when it closes.
  const filtersTouchedRef = useRef(false);
  const onFiltersUpdatedRef = useRef(onFiltersUpdated);
  onFiltersUpdatedRef.current = onFiltersUpdated;
  useEffect(
    () => () => {
      if (filtersTouchedRef.current) onFiltersUpdatedRef.current();
    },
    [],
  );
  const dispatch = useDispatch<AppDispatch>();
  const useMultiSelectRoles =
    multiSelectPlayerRoles ||
    builderContext === "evolution-builder-with-player";

  const handleSelectActiveFilter = (filter: EvolutionPickerSelection) => {
    if (builderContext === "evolution-builder-with-player") {
      dispatch(setEvolutionBuilderActiveFilter(filter));
    } else {
      dispatch(setActivePickerSelection(filter));
    }
  };

  const roleGroups = buildRoleGroups({
    playerRoles,
    positionsOrder: defaultPositions.map(({ label }) => label),
    playablePositions: hideRolesPlayerCannotPlay,
  });
  const selectedRoleIds = (selectedPlayerRoles ?? []).map((r) => r.id);
  const toggleRole = (id: number | string) => {
    const role = playerRoles.find((r) => r.id === id);
    if (!role) return;
    const current = selectedPlayerRoles ?? [];
    handleSelectPlayerRoles?.(
      current.some((r) => r.id === id)
        ? current.filter((r) => r.id !== id)
        : [...current, role],
    );
  };
  const activeFilterCount = activeFiltersOf(playerFilters ?? []).length;
  const close = () => handleToolbarAction("none");

  return (
    <StickySidebar
      handleClose={close}
      title={
        elementOpened === "playerRole"
          ? undefined
          : elementOpened === "includeExclude"
            ? t("picker.sidebarTitle")
            : t("common:filters")
      }
      subtitle={
        elementOpened === "filters" && activeFilterCount > 0
          ? t("players:filters.panel.activeCount", {
              count: activeFilterCount,
            })
          : undefined
      }
      action={
        elementOpened === "filters" ? (
          <DsButton
            variant="secondary"
            size="small"
            // Closing applies: the builder's draft filters commit on unmount.
            onClick={() => handleToolbarAction("none")}
          >
            {t("common:apply")}
          </DsButton>
        ) : undefined
      }
    >
      {elementOpened === "playerRole" ? (
        <div ref={refPlayerRole} className="pt-4">
          <RoleSelectSheetContent
            groups={roleGroups}
            multi={useMultiSelectRoles}
            selectedRoleIds={selectedRoleIds}
            onToggle={(role) => toggleRole(role.id)}
            selectedRoleId={playerRole?.id}
            subtitle={t("players:roleSelect.subtitle")}
            onPick={(role) => {
              if (useMultiSelectRoles) {
                handleSelectPlayerRoles?.([]);
                return;
              }
              handleSelectPlayerRole?.(
                playerRoles.find((r) => r.id === role?.id),
              );
              handleToolbarAction("none");
            }}
            onClose={close}
          />
        </div>
      ) : elementOpened === "includeExclude" ? (
        <IncludeExcludeEvolutions
          activeFilter={
            builderContext === "evolution-builder-with-player"
              ? activeFilter
              : activePickerSelection
          }
          handleSelectActiveFilter={handleSelectActiveFilter}
          initialIncludedEvolutions={
            builderContext === "evolution-builder-with-player"
              ? builderIncludedEvolutions
              : evoPlayersIncludedEvolutions
          }
          initialMustIncludeEvolutions={
            builderContext === "evolution-builder-with-player"
              ? builderMustIncludeEvolutions
              : evoPlayersMustIncludeEvolutions
          }
          ref={refIncludeExclude}
          onClose={() => handleToolbarAction("none")}
          onApply={(data) => {
            if (builderContext === "evolution-builder-with-player") {
              dispatch(applyEvolutionFilters(data));
            } else {
              dispatch(setIncludedEvolutions(data.includedEvolutions));
              dispatch(setMustIncludeEvolutions(data.mustIncludeEvolutions));
            }
            if (isTouchDevice) handleToolbarAction("none");
          }}
          showExpired={!!builderContext}
          ignoreExcludedByDefault={
            builderContext === "evolution-builder-with-player"
          }
        />
      ) : builderContext === "evolution-builder-with-player" ? (
        <EvolutionBuilderFilters
          ref={refFilters}
          onFiltersUpdated={onFiltersUpdated}
        />
      ) : (
        <div ref={refFilters}>
          <PlayerFiltersPanel
            filters={playerFilters ?? []}
            activeCount={activeFilterCount}
            summary={
              activeFilterCount > 0
                ? t("players:filters.panel.activeCount", {
                    count: activeFilterCount,
                  })
                : t("common:noFiltersApplied")
            }
            hidden={builderContext ? [] : ["offBalanceDribbling"]}
            showEvolutionFilters={!builderContext}
            onUpdateFilter={(f) => {
              filtersTouchedRef.current = true;
              if (window.scrollY > 220) {
                window.scrollTo(0, 220);
              }
              dispatch(updateFilter(f));
              if (sortBy === "date-added") {
                setSortBy?.("meta-rating");
              }
            }}
            onClearFilter={(key) => dispatch(clearFilter(key))}
            onClearAll={() => dispatch(clearAllFilters())}
          />
        </div>
      )}
    </StickySidebar>
  );
};
